import classnames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SwiperCore, { Navigation, Thumbs } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { getDamPath } from "@utils/configuration/aem";
import { EmptyImages, ProductImageRenditions } from "@utils/enums";
import { stopVideo } from "@utils/imageViewer";

import { SlideThumbnail } from "@components/Product/ImageViewer/partials/SlideThumbnail/slideThumbnail.component";
import { SwiperNavButton } from "@components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { ZoomLink } from "@components/Product/ImageViewer/partials/ZoomLink/zoomLink.component";
import { Image } from "@components/Product/ProductImage/imageWrapper/image/image.component";

import { ImageViewerImage, ImageViewerVideo } from "./imageViewer.container";
import "./imageViewer.scss";

type Props = {
    images: Array<ImageViewerImage>;
    videos: Array<ImageViewerVideo>;
    activeIndex: any;
    setActiveIndex: any;
    usesDefaultImage: boolean;
    defaultVideoThumbnailPath?: string;
    ecoChequesAccepted: boolean;
};

const ImageViewer = ({
    images,
    videos,
    usesDefaultImage,
    setActiveIndex,
    activeIndex,
    defaultVideoThumbnailPath,
    ecoChequesAccepted,
}: Props) => {
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [thumbSwiper, setThumbSwiper] = useState<SwiperCore>();

    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const mediaList = useMemo(() => [...images, ...videos], [images, videos]);

    const zoomLinkValue = "#imagezoom";
    const amount = mediaList.length;

    useEffect(() => {
        const hashChangeHandler = () => {
            stopVideo();
        };

        window.addEventListener("hashchange", hashChangeHandler);
        return () =>
            window.removeEventListener("hashchange", hashChangeHandler);
    }, []);

    useEffect(() => {
        if (mainSwiper && thumbSwiper) {
            if (thumbSwiper) {
                thumbSwiper.update();
            }

            if (mainSwiper?.activeIndex !== activeIndex) {
                mainSwiper.slideTo(activeIndex);
            }
        }
    }, [activeIndex]);

    const showSlider =
        (images && images.length > 1) ||
        (images.length >= 1 && videos.length > 0);

    return (
        <div className="imageViewer pr-3" data-test="imageViewer">
            <div
                data-test="imageViewer-gallery-slider"
                className="imageViewer__gallery-slider"
            >
                <a
                    data-test="imageViewer-gallery-slider-link"
                    href={!usesDefaultImage ? zoomLinkValue : "#"}
                    onClick={() =>
                        mainSwiper && setActiveIndex(mainSwiper.activeIndex)
                    }
                >
                    <Swiper
                        modules={[Thumbs]}
                        thumbs={{ swiper: thumbSwiper }}
                        spaceBetween={10}
                        slidesPerView={1}
                        centeredSlides
                        onActiveIndexChange={swiper => {
                            setActiveIndex(swiper.activeIndex);
                            stopVideo();
                        }}
                        onSwiper={setMainSwiper}
                    >
                        {images &&
                            images.map((image, index) => (
                                <SwiperSlide
                                    key={"main" + index}
                                    data-test={`imageViewer-gallery-slider-image-${image.altText}`}
                                >
                                    <Image
                                        lazyLoad={index > 0}
                                        parentFit={false}
                                        altText={image.altText}
                                        defaultImageUrl={
                                            EmptyImages.Size_120_90
                                        }
                                        medias={[
                                            {
                                                altText: image.altText,
                                                url: image.main,
                                                format: ProductImageRenditions.XS,
                                            },
                                        ]}
                                        rendition={ProductImageRenditions.XS}
                                    />
                                    {ecoChequesAccepted && (
                                        <img
                                            className="imageViewer__eco-label"
                                            src={`${getDamPath()}/site-afbeeldingen/eco.svg`}
                                            alt="ecocheques"
                                        />
                                    )}
                                </SwiperSlide>
                            ))}
                        {videos &&
                            videos.map((video, index) => (
                                <SwiperSlide
                                    key={"main-video-" + index}
                                    data-test="imageViewer-gallery-slider-video"
                                    className="imageViewer__gallery-slide
                                            imageViewer-mediaSlide
                                            embed-responsive
                                            embed-responsive-16by9"
                                >
                                    <iframe
                                        className="d-block w-100 h-100 product-video"
                                        id={`imageViewerIframe-${index}`}
                                        src={`${video.main}`}
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                </a>
            </div>
            {!usesDefaultImage && (
                <ZoomLink
                    className={ecoChequesAccepted ? "pt-5 pt-md-0" : ""}
                    data-test="imageViewer-zoomLink"
                    onClick={() =>
                        mainSwiper && setActiveIndex(mainSwiper.activeIndex)
                    }
                    zoomLinkValue={zoomLinkValue}
                />
            )}

            {showSlider && (
                <div>
                    <div
                        data-test="imageViewer-thumbnail-slider"
                        className="imageViewer__thumbnail-slider d-none d-md-flex align-items-center slider-layout-auto"
                    >
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationPrevRef}
                                disabled={activeIndex === 0}
                                icon="left"
                                onClick={() => {
                                    if (activeIndex !== 0) {
                                        mainSwiper && mainSwiper.slidePrev();
                                        thumbSwiper && thumbSwiper.slidePrev();
                                    }
                                }}
                            />
                        )}
                        <Swiper
                            onSwiper={setThumbSwiper}
                            spaceBetween={10}
                            slidesPerView={"auto"}
                            threshold={2}
                            navigation={{
                                nextEl: navigationNextRef.current,
                                prevEl: navigationPrevRef.current,
                            }}
                            modules={[Thumbs, Navigation]}
                            watchSlidesProgress
                        >
                            {mediaList.map((media, index) => (
                                <SwiperSlide
                                    tag="button"
                                    key={"thumb" + index}
                                    className={classnames(
                                        "ssr-slide my-1 imageViewer-thumbnail bg-transparent",
                                        index === activeIndex &&
                                            "imageViewer-activeThumb",
                                    )}
                                    onClick={() => {
                                        setActiveIndex(index);
                                    }}
                                >
                                    <SlideThumbnail
                                        lazyLoad={index > 0}
                                        media={media}
                                        defaultVideoThumbnailPath={
                                            defaultVideoThumbnailPath
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationNextRef}
                                disabled={activeIndex + 1 === amount}
                                icon="right"
                                onClick={() => {
                                    if (activeIndex + 1 !== amount) {
                                        mainSwiper && mainSwiper.slideNext();
                                        thumbSwiper && thumbSwiper.slideNext();
                                    }
                                }}
                            />
                        )}
                    </div>
                    <div
                        data-test="imageViewer-amountHolder"
                        className="imageViewer-amountHolder d-flex d-md-none align-items-center justify-content-center"
                    >
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationPrevRef}
                                disabled={activeIndex === 0}
                                icon="left"
                                onClick={() => {
                                    if (activeIndex !== 0 && mainSwiper) {
                                        mainSwiper.slidePrev();
                                        setActiveIndex(mainSwiper.activeIndex);
                                    }
                                }}
                            />
                        )}
                        <p
                            data-test="imageViewer-amountHolder-amount"
                            className="m-0"
                        >
                            {`${activeIndex + 1} van ${amount}`}
                        </p>
                        {amount > 1 && (
                            <SwiperNavButton
                                innerRef={navigationNextRef}
                                disabled={activeIndex + 1 === amount}
                                icon="right"
                                onClick={() => {
                                    if (
                                        activeIndex + 1 !== amount &&
                                        mainSwiper
                                    ) {
                                        mainSwiper.slideNext();
                                        setActiveIndex(mainSwiper.activeIndex);
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export { ImageViewer };
